import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import {
  getAllRewardTiers,
  deleteRewardTier,
  createRewardTier,
  updateRewardTier,
} from '@/api/rewardTier/rewardTier.actions';
import {
  RewardTier,
  CreateRewardTierData,
} from '@/api/rewardTier/rewardTier.types';
import { isApiSuccess } from '@/api/shared.types.ts';
import { useAsync } from '@/hooks/global/useAsync';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useRewardTiers = () => {
  const [rewardTiers, setRewardTiers] = useState<RewardTier[]>([]);
  const [selectedRewardTier, setSelectedRewardTier] =
    useState<RewardTier | null>(null);
  const { run, isLoading } = useAsync();
  const { run: runFetchAll, isLoading: isFetchingAll } = useAsync();

  const fetchTiers = async (): Promise<void> => {
    const response = await runFetchAll(() => getAllRewardTiers());
    if (!isApiSuccess(response)) {
      toast.error(response.error.message);
      return;
    }
    setRewardTiers(response.content.rewardTiers);
  };

  const createTier = async (data: CreateRewardTierData): Promise<void> => {
    const response = await run(() => createRewardTier(data));
    if (!isApiSuccess(response)) {
      toast.error(response.error.message);
      return;
    }
    setRewardTiers((prev) => [...prev, response.content.rewardTier]);
    toast.success('Reward tier created successfully.');
  };

  const updateTier = async (
    id: string,
    data: CreateRewardTierData
  ): Promise<void> => {
    const response = await run(() => updateRewardTier(id, data));
    if (!isApiSuccess(response)) {
      toast.error(response.error.message);
      return;
    }
    setRewardTiers((prev) =>
      prev.map((tier) =>
        tier.id === response.content.rewardTier.id
          ? response.content.rewardTier
          : tier
      )
    );
    toast.success('Reward tier updated successfully.');
  };

  const deleteTier = async (id: string): Promise<void> => {
    const response = await run(() => deleteRewardTier(id));
    if (!isApiSuccess(response)) {
      toast.error(response.error.message);
      return;
    }
    setRewardTiers((prev) =>
      prev.filter((tier) => tier.id !== response.content.rewardTier.id)
    );
    toast.success('Reward tier deleted successfully.');
  };

  useEffect(() => {
    fetchTiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    rewardTiers,
    selectedRewardTier,
    setSelectedRewardTier,
    createTier,
    updateTier,
    deleteTier,
    refetch: fetchTiers,
    isLoading,
    isFetchingAll,
  };
};
